import type { ExecutorToolRuntimeContext } from "./executorToolRuntime";
import type { ExecutorStepState } from "./types";

export type ChartStoppingTool = "range_chart" | "active_graph" | "native_chart";

export interface ChartStoppingEvidence {
  stepId: string;
  tool: ChartStoppingTool;
  operation: string;
  satisfied: boolean;
  reason?: string;
  /**
   * Something the user should know that does not invalidate the result — an
   * unpopulated value after the retry budget, for instance. Kept apart from
   * `satisfied` so a chart that exists and is verified is never reported as a
   * failure just because Excel is still catching up.
   */
  warning?: string;
  details?: Record<string, unknown>;
}

function evidenceStore(
  ctx: ExecutorToolRuntimeContext,
): Map<string, ChartStoppingEvidence> {
  if (!ctx.session.chartStoppingEvidence) {
    ctx.session.chartStoppingEvidence = new Map();
  }
  return ctx.session.chartStoppingEvidence;
}

export function resolveChartStoppingStepId(
  ctx: ExecutorToolRuntimeContext,
  requestedStepId: unknown,
): string | undefined {
  if (typeof requestedStepId === "string" && requestedStepId.trim()) {
    const requested = requestedStepId.trim();
    return ctx.planState.steps.some((step) => step.id === requested)
      ? requested
      : undefined;
  }
  if (ctx.planState.currentStepId) return ctx.planState.currentStepId;
  const candidates = ctx.planState.steps.filter(
    (step) =>
      chartStepMatchesWording(step) &&
      !["success", "warning", "failed", "recovered"].includes(step.status),
  );
  return candidates.length === 1 ? candidates[0].id : undefined;
}

export function beginChartStoppingEvidence(
  ctx: ExecutorToolRuntimeContext,
  input: {
    stepId?: unknown;
    tool: ChartStoppingTool;
    operation: string;
  },
): string | undefined {
  const stepId = resolveChartStoppingStepId(ctx, input.stepId);
  if (!stepId) return undefined;
  evidenceStore(ctx).set(stepId, {
    stepId,
    tool: input.tool,
    operation: input.operation,
    satisfied: false,
    reason: "Chart operation has not produced its required verification evidence.",
  });
  return stepId;
}

export function finishChartStoppingEvidence(
  ctx: ExecutorToolRuntimeContext,
  evidence: ChartStoppingEvidence,
): void {
  evidenceStore(ctx).set(evidence.stepId, evidence);
}

export function getChartStoppingEvidence(
  ctx: ExecutorToolRuntimeContext,
  stepId: string,
): ChartStoppingEvidence | undefined {
  return ctx.session.chartStoppingEvidence?.get(stepId);
}

const CHART_PLAN_ACTIONS = new Set([
  "chart",
  "graph",
  "range_chart",
  "native_chart",
  "active_graph",
]);

/**
 * Wording is only a backstop. It catches a step that claims to have made a
 * chart without ever calling a chart tool — the case where there is no tool
 * usage to key off — but it is deliberately not the primary trigger, since a
 * step phrased "add the price visual" would slip past any word list.
 */
function chartStepMatchesWording(step: ExecutorStepState): boolean {
  const text =
    `${step.planStep.action ?? ""} ${step.planStep.description ?? ""}`.toLowerCase();
  return /\b(chart|graph|plot)\b|activegraph|range_chart|native_chart|visuali[sz]/.test(
    text,
  );
}

/**
 * Plan actions are authoritative for terminal gating. A fill step may mention
 * "chart inputs" without itself promising chart output.
 */
function chartStepDeclaredAsOutput(step: ExecutorStepState): boolean {
  const action = (step.planStep.action ?? "").trim().toLowerCase();
  if (action) return CHART_PLAN_ACTIONS.has(action);
  return chartStepMatchesWording(step);
}

/**
 * A step needs chart evidence when a chart tool actually ran for it, or when
 * it reads like a charting step. Tool usage is authoritative: it is recorded
 * by `beginChartStoppingEvidence` the moment a chart tool starts, so it cannot
 * be dodged by rewording the plan.
 */
export function chartStepRequiresEvidence(
  step: ExecutorStepState,
  evidence?: ReadonlyMap<string, ChartStoppingEvidence>,
): boolean {
  return evidence?.has(step.id) === true || chartStepDeclaredAsOutput(step);
}

export function chartStoppingStatusError(
  ctx: ExecutorToolRuntimeContext,
  step: ExecutorStepState,
  status: string,
): string | undefined {
  if (!["success", "warning", "recovered"].includes(status)) return undefined;
  if (!chartStepRequiresEvidence(step, ctx.session.chartStoppingEvidence)) {
    return undefined;
  }
  const evidence = getChartStoppingEvidence(ctx, step.id);
  if (!evidence) {
    // `warning` stays available. This run may be a continuation of one that
    // already built the chart, and evidence is per-run: refusing every terminal
    // status would leave a resumed step with nothing it can honestly move to.
    // Unearned `success` is still refused, which is what the gate is for.
    if (status === "warning") return undefined;
    return (
      `Cannot mark chart step "${step.id}" ${status}: no output-specific chart ` +
      "verification evidence was recorded. Run the appropriate chart tool and verify its read-back first, " +
      'or mark the step "warning" if this run cannot verify it again.'
    );
  }
  // A chart tool ran and its verification came back short. `warning` is the
  // honest terminal state for that, and refusing it only pushes the model into
  // re-running the same failing read: the gate exists to stop unearned
  // success, not to keep a step alive that has nowhere left to go.
  if (!evidence.satisfied && status !== "warning") {
    return (
      `Cannot mark chart step "${step.id}" ${status}: ${evidence.reason ?? "required chart verification failed"}` +
      ' Mark the step "warning" instead if the chart operation already ran and cannot be verified further.'
    );
  }
  return undefined;
}
